import cx from 'classnames';
import styled from '@emotion/styled';
import React, { useState } from 'react';
import { animated, useTransition } from 'react-spring';
import VideoLimitAlertUpgradeButton, {
  ButtonType,
  UpgradeRefType,
} from './VideoLimitAlertUpgradeButton';
import { useFeatures } from '~/react-hooks/use-features';

const NavAlert = styled.div`
  margin: auto;

  @media (max-width: 1024px) {
    max-width: 400px;
    text-align: center;

    .navigation-alert {
      line-height: normal;
    }
    .text {
      display: block;
      padding: 5px 0;
    }
  }

  @media (max-width: 640px) {
    margin: 10px auto;
    max-width: 90%;

    .navigation-alert {
      min-width: 0;
    }
  }
`;

interface VideoLimitProps {
  limitReached: boolean;
  limitWarning: boolean;
  videoLimit: number | null;
  videoRemaining: number;
}

const Message = ({
  limitReached,
  limitWarning,
  videoLimit,
  videoRemaining,
}: VideoLimitProps) => {
  const { canSwitchPlan } = useFeatures();

  if (limitReached) {
    return (
      <span className='text'>
        You&apos;ve hit your {videoLimit} sending limit. &nbsp;
        <VideoLimitAlertUpgradeButton
          className='!text-white hover:text-white underline'
          upgradeRefType={UpgradeRefType.GreetLimitReachedAlert}
          buttonType={canSwitchPlan ? ButtonType.Link : ButtonType.ModalButton}
        />
      </span>
    );
  } else if (limitWarning) {
    return (
      <span className='text'>
        You are about to reach your limit of {videoLimit} videos(
        {videoRemaining} left).&nbsp;
        <VideoLimitAlertUpgradeButton
          className='!text-pink-900 hover:text-pink-900 underline'
          upgradeRefType={UpgradeRefType.GreetLimitWarningAlert}
          buttonType={canSwitchPlan ? ButtonType.Link : ButtonType.ModalButton}
        />
      </span>
    );
  }
  return null;
};

export default function VideoLimitAlert({
  limitReached,
  limitWarning,
  videoLimit,
  videoRemaining,
}: VideoLimitProps) {
  const [hideAlert, setAlertState] = useState(!limitWarning && !limitReached);
  const handleClick = () => {
    setAlertState(true);
  };

  const transitions = useTransition(hideAlert, null, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  return (
    <NavAlert className='absolute xl:!mr-32'>
      {transitions.map(({ key, item: hideAlert, props }) => {
        props = { ...props };

        if (hideAlert) {
          return null;
        }

        return (
          <animated.div key={key} style={props}>
            <div
              id='platform-alert'
              className={cx('navigation-alert', {
                danger: limitReached,
                warning: limitWarning && !limitReached,
              })}
            >
              <Message
                limitReached={limitReached}
                limitWarning={limitWarning}
                videoLimit={videoLimit}
                videoRemaining={videoRemaining}
              />
              <button
                type='button'
                className='close'
                aria-label='Close'
                onClick={handleClick}
              >
                <span aria-hidden='true'>&times;</span>
              </button>
            </div>
          </animated.div>
        );
      })}
    </NavAlert>
  );
}
